import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/value-lists.js

/**
 * ValueListHandler
 * 
 * Manages reference data (value lists) throughout the application with features:
 * - Efficient API data fetching with pagination support
 * - Data transformation to consistent application-specific formats
 * - Multi-layer caching strategy (memory, session storage, GraphQL)
 * - Smart refresh policy based on data age
 * - Parallel loading of multiple data sets
 * - Preloading from Gatsby GraphQL cache for SSR/SSG optimization
 * - Resilient error handling for individual list failures
 */
export class ValueListHandler {
  /**
   * Initialize the value list handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;

    // Define API endpoints for each value list type
    this.endpoints = {
      countries: 'app-countries',
      hotspotSectors: 'app-hotspot-sectors',
      scope3Sectors: 'ca-scope3-sectors',
      activities: 'ca-activities-list',
      prefills: 'ca-prefills-list'
    };

    // Data transformers for each value list type
    this.transformers = {
      countries: item => ({
        id: item.id,
        countryId: item.CountryId,
        code: item.CountryCode,
        name: item.CountryName,
        isActive: item.isActive
      }),
      hotspotSectors: item => ({
        id: item.id,
        code: item.HotspotSectorId,
        name: item.HotspotSectorName,
        friendlyName: item.FriendlyName
      }),
      scope3Sectors: item => ({
        id: item.id,
        Scope3SectorId: item.Scope3SectorId,
        code: item.Scope3SectorCode,
        name: item.Scope3SectorName,
        hotspotSectorId: item.HotspotSectorId,
        isActive: item.IsActive === true,
        includedInFreeVersion: item.IsIncludedInFreeVersion === true
      }),
      activities: item => ({
        id: item.id,
        activityId: item.ActivityId,
        name: item.ActivityName,
        unit: item.ActivityUnit,
        dataCollectionSheet: item.DataCollectionSheet,
        hierarchyLevel1: item.HierarchyLevel1,
        hierarchyLevel2: item.HierarchyLevel2,
        hierarchyLevel3: item.HierarchyLevel3,
        isActive: item.IsActive === true,
        includedInFreeVersion: item.IsIncludedInFreeVersion === true,
        requiresExactValueInput: item.RequiresExactValueInput === true
      }),
      prefills: item => ({
        id: item.id,
        industryId: item.IndustryId,
        industryName: item.IndustryName,
        spendVolumeShare: item.spendVolumeShare,
        spendInputs: item.spendInputs
      })
    };

    // List of all value list types
    this.valueListTypes = Object.keys(this.endpoints);
  }

  /**
   * Initialize all value lists with efficient caching
   *
   * Caching strategy:
   * 1. If data exists in store (from cache restore), TRUST IT - don't reload
   * 2. Use localStorage for timestamp (survives browser close, unlike sessionStorage)
   * 3. Only reload if data is missing OR timestamp is expired (24 hours)
   *
   * @returns {Promise<boolean>} Success status (true if all lists loaded successfully)
   */
  async initialize() {
    try {
      // ✅ FIX: If ALL data exists in store, trust it completely
      // This prevents unnecessary API calls after cache restore
      const allDataExists = this.valueListTypes.every(
        type => this.store.state.data[type]?.length > 0
      );

      if (allDataExists) {
        return true;
      }

      // Check which lists are missing
      const missingLists = this.valueListTypes.filter(
        type => !this.store.state.data[type]?.length
      );

      // Missing lists logging silenced

      // ✅ FIX: Use localStorage instead of sessionStorage (survives browser close)
      const cacheKey = 'valueListsTimestamp';
      const cacheExpiryHours = 24;
      const valueListsTimestamp = this.getCacheTimestamp(cacheKey);
      const cacheExpired = !valueListsTimestamp ||
        this.isCacheExpired(valueListsTimestamp, cacheExpiryHours);

      // Only load lists that are actually missing
      // Don't reload existing data just because timestamp expired
      if (missingLists.length === 0) {
        // All data exists, just update timestamp if needed
        if (cacheExpired) {
          this.updateCacheTimestamp(cacheKey);
        }
        return true;
      }

      // Load only missing value lists in parallel
      const loadPromises = missingLists.map(type => this.loadValueList(type));

      const results = await Promise.allSettled(loadPromises);

      // Check for failures
      const failures = results.filter(result => result.status === 'rejected').length;

      // Update cache timestamp
      this.updateCacheTimestamp(cacheKey);

      // Value lists loading logging silenced

      return failures === 0;
    } catch (error) {
      this.store.handleError('valueListsInit', error);
      return false;
    }
  }

  /**
   * Preload data from Gatsby's GraphQL cache into the store
   * @param {string} type - Value list type to preload
   * @returns {boolean} True if data was successfully loaded from GraphQL
   */
  preloadFromGraphQL(type) {
    if (typeof window === 'undefined' || !window.__GATSBY_INITIAL_DATA__) {
      return false;
    }

    try {
      // Skip if data already exists in store
      if (this.store.state.data[type]?.length > 0) {
        return true;
      }

      const graphqlMap = {
        countries: 'allCountry',
        hotspotSectors: 'allHotspotSector',
        scope3Sectors: 'allScope3Sector',
        activities: 'allActivity',
        prefills: 'allPrefill'
      };

      // Look for data in the staticQueryResults
      if (window.__GATSBY_INITIAL_DATA__.staticQueryResults) {
        const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults;

        for (const queryId in staticQueries) {
          const queryResult = staticQueries[queryId];

          if (queryResult?.data?.[graphqlMap[type]]?.nodes?.length > 0) {
            // Found data for this type in GraphQL cache
            const graphqlData = queryResult.data[graphqlMap[type]].nodes;

            // Store it in the app store
            this.store.state.data[type] = graphqlData;

            // Notify subscribers
            this.store.notifySubscribers(type);

            return true;
          }
        }
      }

      return false;
    } catch (error) {
      this.store.handleError(`preloadGraphQL_${type}`, error);
      return false;
    }
  }

  /**
   * Load a specific value list from API with pagination support
   * @param {string} type - Type of value list to load
   * @returns {Promise<Array>} Transformed data ready for application use
   * @throws {Error} If the value list type is unknown or API request fails
   */
  async loadValueList(type) {
    if (!this.endpoints[type]) {
      return Promise.reject(new Error(`Unknown value list type: ${type}`));
    }

    try {
      // Skip if already loaded
      if (this.store.state.data[type].length > 0) {
        return this.store.state.data[type];
      }

      this.store.state.loading[type] = true;

      const allResults = [];
      let page = 1;
      let hasMorePages = true;
      const pageSize = 100; // Larger page size for efficiency

      // Paginated API requests
      while (hasMorePages) {
        const url = `${process.env.GATSBY_STRAPI_URL}/api/${this.endpoints[type]}?pagination[page]=${page}&pagination[pageSize]=${pageSize}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${type}: ${response.status} ${response.statusText}`);
        }

        const { data, meta } = await response.json();
        allResults.push(...data);

        // Check if we've got all pages
        hasMorePages = page < meta.pagination.pageCount;
        page++;
      }

      // Transform the results to application format
      const transformed = this.transformData(type, { data: allResults });

      // Update store and notify
      this.store.state.data[type] = transformed;
      this.store.notifySubscribers(type);

      return transformed;
    } catch (error) {
      this.store.handleError(type, error);
      throw error;
    } finally {
      this.store.state.loading[type] = false;
    }
  }

  /**
   * Transform raw data from API into consistent application format
   * @param {string} type - Type of data to transform
   * @param {Object} data - Raw data from API
   * @returns {Array} Transformed data ready for application use
   */
  transformData(type, data) {
    if (!data?.data || !Array.isArray(data.data)) {
      return [];
    }

    const transformer = this.transformers[type];
    if (!transformer) {
      return [];
    }

    // Apply transformer to each item
    return data.data.map(item => {
      try {
        return transformer(item.attributes || item);
      } catch (error) {
        // Skip items that can't be transformed, but continue processing
        return null;
      }
    }).filter(Boolean); // Remove null items
  }

  /**
   * Get timestamp from cache
   * ✅ FIX: Use localStorage (survives browser close) instead of sessionStorage
   * @private
   * @param {string} key - Cache key to retrieve
   * @returns {number|null} Timestamp or null if not found
   */
  getCacheTimestamp(key) {
    try {
      // Try localStorage first (preferred - survives browser close)
      if (typeof localStorage !== 'undefined') {
        const value = localStorage.getItem(key);
        if (value) return parseInt(value, 10);
      }
      // Fallback to sessionStorage
      if (typeof sessionStorage !== 'undefined') {
        const value = sessionStorage.getItem(key);
        if (value) return parseInt(value, 10);
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update cache timestamp
   * ✅ FIX: Use localStorage (survives browser close) instead of sessionStorage
   * @private
   * @param {string} key - Cache key to update
   */
  updateCacheTimestamp(key) {
    try {
      const timestamp = Date.now().toString();
      // Save to localStorage (preferred)
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, timestamp);
      }
      // Also save to sessionStorage for backwards compatibility
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(key, timestamp);
      }
    } catch (error) {
      // Ignore storage errors (e.g., quota exceeded)
    }
  }

  /**
   * Check if cache is expired
   * @private
   * @param {number} timestamp - Timestamp to check
   * @param {number} expiryHours - Hours after which cache expires
   * @returns {boolean} True if cache is expired
   */
  isCacheExpired(timestamp, expiryHours) {
    const expiryMs = expiryHours * 60 * 60 * 1000;
    return Date.now() - timestamp > expiryMs;
  }
}